import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { SharedModule } from '@components/shared/shared.module';
import { Session as Topic } from '@models/Sessions/Session';
import { AuthenticationService } from '@services/auth/authentication.service';
import { BusService } from '@services/bus.service';
import { ConfigService } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { SessionsRequestService } from '@services/sessions/sessions.request.service';
import { ButtonModule } from 'primeng/button';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FluidModule } from 'primeng/fluid';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';

interface Form {
  title: FormControl<string>;
  oneLiner: FormControl<string | null>;
  description: FormControl<string | null>;
}

@Component({
  selector: 'app-new-topic-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    FieldsetModule,
    TextareaModule,
    ButtonModule,
    FloatLabelModule,
    FluidModule,
    SharedModule,
  ],
  templateUrl: './new-topic-form.component.html',
  styleUrl: './new-topic-form.component.scss',
})
export class NewTopicFormComponent implements OnInit, OnDestroy {
  config = inject(ConfigService);
  formBuilder = inject(FormBuilder);
  bus = inject(BusService);
  sessionRequestService = inject(SessionsRequestService);
  authService = inject(AuthenticationService);
  eventsService = inject(EventsService);
  router = inject(Router);

  form: FormGroup<Form>;
  topic: Topic;

  // Metadata
  isSubmitting = signal(false);
  isUploading = signal(false);

  constructor() {
    this.createForm();
  }

  ngOnInit() {
    this.initTopic();
    this.subscribe();
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  private createForm() {
    this.form = this.formBuilder.group({
      title: this.formBuilder.control('', [Validators.required]),
      oneLiner: this.formBuilder.control('', [
        Validators.maxLength(this.config.inputLengths.SESSION_ONE_LINER),
      ]),
      description: this.formBuilder.control('', [
        Validators.maxLength(this.config.inputLengths.SESSION_DESCRIPTION),
      ]),
    });
  }
  handleUploadStart() {
    this.isUploading.set(true);
  }
  handleUploadComplete(thumbnail_id: string) {
    this.isUploading.set(false);
    this.topic.config.thumbnail_id = thumbnail_id;
  }
  initTopic() {
    this.topic = {
      config: {
        cluster: false,
        matrix: {
          active: false,
          role_view: {
            moderators: false,
            participants: false,
            externals: false,
          },
        },
        voting: { active: true },
        ideas: { active: true },
        comments: { active: true },
        participants: [],
        teams: [],
        thumbnail_id: '',
        title: '',
        one_liner: '',
        description: '',
        axis: {
          x: null,
          y: null,
        },
        morphbox: { active: false }, // to delete
        random_images: { active: false }, // to delete
        random_words: { active: false }, // to delete
        people: { active: true },
        workspace: { active: false },
        teams_page: { active: false }, // to delete
        personal_results: { active: false }, // to delete
        pdf_exports: { active: false },
        solutions: false,
        benefits_and_challenges: false,
      },
      created_at: null,
      created_by: null,
      data: {
        announcements: [],
        ideas: [],
        followers: [],
        morphboxes: [],
        notifications: [],
      },
      scores: null,
      i_follow: false,
      me: null,
      _id: null,
      lockActions: false,
      canRevoke: false,
    };
  }

  handleSubmit() {
    if (!this.form.valid) return;
    this.isSubmitting.set(true);

    this.topic.config = {
      ...this.topic.config,
      participants: [
        ...this.topic.config.participants,
        {
          role: this.authService.hasRole('admin') ? 0 : 1,
          email: this.authService.currentUserSync.email,
          votes_for_lookup: [],
          team_idx: 0,
          _id: this.authService.currentUserSync._id,
          team: null,
          votes_for: [],
        },
      ],
      title: this.form.controls.title.value,
      one_liner: this.form.controls.oneLiner.value,
      description: this.form.controls.description.value,
    };

    this.bus.publish(
      this.eventsService.requested.action.session.create,
      this.topic,
    );

    this.bus.publish(
      this.eventsService.requested.action.tracking.track,
      this.topic,
      'session',
      'create',
      this.topic.config.title,
    );
  }

  createTopicSuccess(topic: Topic) {
    this.router.navigate(['topics', topic._id]);
    this.isSubmitting.set(false);
    this.sessionRequestService.getAll().subscribe();
  }

  subscribe() {
    this.bus.subscribe(
      this.eventsService.received.action.session.create.success,
      this.createTopicSuccess.bind(this),
    );
    this.bus.subscribe(
      this.eventsService.received.action.session.create.failure,
      this.createTopicSuccess.bind(this),
    );
  }

  unsubscribe() {
    this.bus.unsubscribe(
      this.eventsService.received.action.session.create.success,
      this.createTopicSuccess.bind(this),
    );
    this.bus.unsubscribe(
      this.eventsService.received.action.session.create.failure,
      this.createTopicSuccess.bind(this),
    );
  }
}
